import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import { getAllProducts } from '../api/products';
import ProductCard from '../components/ProductCard';
import Services from '../components/Services';
import Footer from '../components/Footer';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setProductsData } from '../store/slice/productsSlice';

const Home = () => {
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [showScrollButton, setShowScrollButton] = useState(false); 

    const dispatch = useAppDispatch();
    const filteredProducts = useAppSelector((state) => state.products.filteredProducts);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await getAllProducts();
                if (response) {
                    setLoading(false);
                    setProducts(response);
                    dispatch(setProductsData(response));
                } else {
                    console.log("Failed to get products");
                    setLoading(false);
                }
            } catch (error) {
                console.log("Failed to fetch products", error);
                setLoading(false);
            }
        };
        fetchProducts();
    }, [dispatch]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollButton(true);
            } else {
                setShowScrollButton(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const displayedProducts = filteredProducts.filter((product: any) =>
        product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sectionTitle = searchTerm ? searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1) : 'Explore Our Products';

    return (
        <div>
            <Header searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <Hero />
            <div className="mt-8 lg:mt-16 lg:mx-24">
                {loading ? (
                    <div className="flex items-center justify-center h-screen">
                        <div role="status">
                            <svg
                                aria-hidden="true"
                                className="w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"
                                />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="border-l-4 border-lightCopper pl-4 ">
                            <p className="text-lightCopper font-bold text-lg md:text-xl">Our Products</p>
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mt-6 mb-6 text-left">
                            {sectionTitle}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {displayedProducts.length > 0 ? (
                                displayedProducts.map((product: any) => (
                                    <ProductCard key={product.id} product={product} />
                                ))
                            ) : (
                                <p className="text-center text-gray-500">No products found.</p>
                            )}
                        </div>
                    </>
                )}
            </div>
            <Services />
            <Footer />
            {showScrollButton && (
                <button
                    onClick={scrollToTop}
                className="fixed bottom-4 right-4 bg-primaryColor hover:bg-[#b3867e] text-white font-bold py-2 px-4 
                rounded-full  transition-all scrolltop"
                >
                    ↑
                </button>
            )}
        </div>
    );
};

export default Home;
